import { Component, HostListener, OnInit } from '@angular/core';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { LayoutService } from '../layout.service';
import {
  ItemVariant,
  VariantParent,
  VariantRuntimeField,
  VariantRuntimeFilter,
  VariantRuntimeSettings,
  VariantSearchRequest,
  VariantSelection,
} from './add-variants.models';

@Component({
  selector: 'app-add-variants-drawer',
  templateUrl: './add-variants-drawer.component.html',
  styleUrls: ['./add-variants-drawer.component.less'],
})
export class AddVariantsDrawerComponent implements OnInit {
  page = 1;
  pageSize = 25;
  totalCount = 0;
  loading = false;
  settingsLoading = false;
  error = '';
  settingsErrors: string[] = [];
  search = '';
  filters: { [key: string]: string[] } = {};
  runtimeFilters: VariantRuntimeFilter[] = [];
  runtimeFields: VariantRuntimeField[] = [];
  items: VariantParent[] = [];
  activeParent: VariantParent = null;
  step: 'styles' | 'variants' = 'styles';
  quantities: { [itemCode: string]: number } = {};
  selections: { [itemCode: string]: VariantSelection } = {};
  private searchChanged$ = new Subject<string>();

  warehouseCode = '';
  priceList = '';
  cardCode = '';

  constructor(
    private drawerRef: NzDrawerRef<VariantSelection[]>,
    private layoutService: LayoutService,
  ) {}

  ngOnInit(): void {
    this.searchChanged$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value) => {
        this.search = value;
        this.page = 1;
        this.load();
      });

    this.loadSettings();
  }

  @HostListener('document:keydown.escape')
  closeOnEscape(): void {
    this.cancel();
  }

  onSearch(value: string): void {
    this.searchChanged$.next(value);
  }

  load(): void {
    if (this.settingsErrors.length > 0) {
      return;
    }

    this.loading = true;
    this.error = '';

    this.layoutService
      .searchVariants({
        ...this.filters,
        search: this.search,
        parentOnly: true,
        page: this.page,
        pageSize: this.pageSize,
        warehouseCode: this.warehouseCode,
        priceList: this.priceList,
        cardCode: this.cardCode,
      })
      .subscribe(
        (response) => {
          const normalized = this.normalizeResponse(response);
          this.items = normalized.items;
          this.totalCount = normalized.totalCount;
          this.loading = false;
        },
        () => {
          this.error = this.layoutService.translate(
            'Unable to load variants. Please try again.',
          );
          this.loading = false;
        },
      );
  }

  applyFilters(): void {
    this.page = 1;
    this.load();
  }

  loadSettings(): void {
    this.settingsLoading = true;
    this.layoutService.getVariantSettings().subscribe(
      (response) => {
        const settings = this.normalizeSettings(response);
        this.runtimeFilters = settings.filters;
        this.runtimeFields = settings.fields;
        this.settingsErrors = settings.isComplete ? [] : settings.errors;
        this.settingsLoading = false;
        if (this.settingsErrors.length === 0) {
          this.load();
        }
      },
      () => {
        this.settingsErrors = [
          this.layoutService.translate('Unable to load variant settings.'),
        ];
        this.settingsLoading = false;
      },
    );
  }

  chooseStyle(item: VariantParent): void {
    this.loading = true;
    this.error = '';

    this.layoutService
      .searchVariants({
        ...this.filters,
        parentItemCode: item.parentItemCode,
        page: 1,
        pageSize: 500,
        warehouseCode: this.warehouseCode,
        priceList: this.priceList,
        cardCode: this.cardCode,
      })
      .subscribe(
        (response) => {
          const normalized = this.normalizeResponse(response);
          this.activeParent = normalized.items[0] || item;
          this.step = 'variants';
          this.loading = false;
        },
        () => {
          this.error = this.layoutService.translate(
            'Unable to load variants. Please try again.',
          );
          this.loading = false;
        },
      );
  }

  backToStyles(): void {
    this.step = 'styles';
    this.activeParent = null;
  }

  pageChanged(page: number): void {
    this.page = page;
    this.load();
  }

  setQuantity(variant: ItemVariant, value: number): void {
    const quantity = Number(value);
    if (!quantity || quantity <= 0) {
      delete this.quantities[variant.itemCode];
      delete this.selections[variant.itemCode];
      return;
    }

    this.quantities = {
      ...this.quantities,
      [variant.itemCode]: quantity,
    };
    this.selections = {
      ...this.selections,
      [variant.itemCode]: {
        parent:
          this.activeParent ||
          this.items.find((item) =>
            item.variants.some((x) => x.itemCode === variant.itemCode),
          ),
        variant,
        quantity,
      },
    };
  }

  selectedCount(): number {
    return Object.keys(this.quantities).filter(
      (key) => Number(this.quantities[key]) > 0,
    ).length;
  }

  totalQuantity(): number {
    return Object.keys(this.quantities).reduce(
      (total, key) => total + Number(this.quantities[key] || 0),
      0,
    );
  }

  selectedItems(): VariantSelection[] {
    return Object.keys(this.selections)
      .map((key) => ({
        ...this.selections[key],
        quantity: Number(this.quantities[key] || 0),
      }))
      .filter((selection) => selection.quantity > 0);
  }

  confirm(): void {
    this.drawerRef.close(this.selectedItems());
  }

  cancel(): void {
    this.drawerRef.close([]);
  }

  trackParent(_: number, item: VariantParent): string {
    return item.parentItemCode;
  }

  trackVariant(_: number, item: ItemVariant): string {
    return item.itemCode;
  }

  colors(parent: VariantParent): string[] {
    return Array.from(
      new Set(
        parent.variants.map((v) => this.variantRoleValue(v, 'matrixRow')),
      ),
    ).filter((x) => x !== '');
  }

  /* sizes(parent: VariantParent): string[] {
    return Array.from(
      new Set(parent.variants.map((v) => this.variantRoleValue(v, 'matrixColumn'))),
    ).filter((x) => x !== '');
  } */

  sizes(parent: VariantParent): string[] {
    const sizeOrder = [
      'XXS',
      'XS',
      'S',
      'M',
      'L',
      'XL',
      '2XL',
      '3XL',
      '4XL',
      '5XL',
      '6XL',
      '7XL',
    ];

    return Array.from(
      new Set(
        parent.variants.map((v) => this.variantRoleValue(v, 'matrixColumn')),
      ),
    )
      .filter((x) => x !== '')
      .sort((a, b) => {
        const aIndex = sizeOrder.indexOf(a);
        const bIndex = sizeOrder.indexOf(b);

        if (aIndex === -1 && bIndex === -1) {
          return a.localeCompare(b, undefined, { numeric: true });
        }
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;

        return aIndex - bIndex;
      });
  }

  variantAt(parent: VariantParent, color: string, size: string): ItemVariant {
    return parent.variants.find(
      (v) =>
        this.variantRoleValue(v, 'matrixRow') === color &&
        this.variantRoleValue(v, 'matrixColumn') === size,
    );
  }

  canUseMatrix(parent: VariantParent): boolean {
    return this.colors(parent).length > 0 && this.sizes(parent).length > 0;
  }

  variantCount(item: VariantParent): number {
    const metadata = (item && item.metadata) || {};
    return Number(metadata.variantCount || metadata.VariantCount || 0);
  }

  trackFilter(_: number, item: VariantRuntimeFilter): string {
    return item.key;
  }

  variantRoleValue(variant: ItemVariant, role: string): string {
    const metadata = (variant && variant.metadata) || {};
    return metadata[role] || '';
  }

  variantFieldValue(variant: ItemVariant, field: VariantRuntimeField): string {
    const metadata = (variant && variant.metadata) || {};
    return metadata[field.key] || metadata[field.role] || '';
  }

  variantDetailFields(): VariantRuntimeField[] {
    return this.runtimeFields.filter(
      (field) => field.scope === 'variant' && !field.role,
    );
  }

  private normalizeResponse(response: any): {
    items: VariantParent[];
    totalCount: number;
  } {
    const rawItems = (response && (response.items || response.Items)) || [];

    return {
      items: rawItems.map((item) => this.normalizeParent(item)),
      totalCount: Number(
        (response && (response.totalCount || response.TotalCount)) || 0,
      ),
    };
  }

  private normalizeSettings(response: any): VariantRuntimeSettings {
    const rawFilters =
      (response && (response.filters || response.Filters)) || [];
    return {
      isComplete:
        response &&
        (response.isComplete !== undefined
          ? response.isComplete
          : response.IsComplete),
      errors: (response && (response.errors || response.Errors)) || [],
      fields: ((response && (response.fields || response.Fields)) || []).map(
        (field) => ({
          key: field.key || field.Key,
          label: field.label || field.Label,
          scope: field.scope || field.Scope,
          role: field.role || field.Role,
        }),
      ),
      filters: rawFilters.map((filter) => ({
        key: filter.key || filter.Key,
        label: filter.label || filter.Label,
        multiple:
          filter.multiple !== undefined ? filter.multiple : filter.Multiple,
        options: (filter.options || filter.Options || []).map((option) => ({
          value: option.value || option.Value,
          label: option.label || option.Label,
        })),
      })),
    };
  }

  private normalizeParent(item: any): VariantParent {
    return {
      parentItemCode: item.parentItemCode || item.ParentItemCode || '',
      parentItemName: item.parentItemName || item.ParentItemName || '',
      brand: item.brand || item.Brand,
      season: item.season || item.Season,
      collection: item.collection || item.Collection,
      subCollection: item.subCollection || item.SubCollection,
      productGroup: item.productGroup || item.ProductGroup,
      imageUrl: item.imageUrl || item.ImageUrl,
      variants: (item.variants || item.Variants || []).map((variant) =>
        this.normalizeVariant(variant),
      ),
      metadata: item.metadata || item.Metadata || {},
    };
  }

  private normalizeVariant(variant: any): ItemVariant {
    return {
      itemCode: variant.itemCode || variant.ItemCode || '',
      itemName: variant.itemName || variant.ItemName || '',
      parentItemCode: variant.parentItemCode || variant.ParentItemCode || '',
      colorCode: variant.colorCode || variant.ColorCode,
      colorName: variant.colorName || variant.ColorName,
      sizeCode: variant.sizeCode || variant.SizeCode,
      sizeName: variant.sizeName || variant.SizeName,
      barcode: variant.barcode || variant.Barcode,
      price: variant.price || variant.Price,
      currency: variant.currency || variant.Currency,
      stock: variant.stock || variant.Stock,
      warehouseCode: variant.warehouseCode || variant.WarehouseCode,
      metadata: variant.metadata || variant.Metadata || {},
    };
  }
}
